import { gql } from '@apollo/client'
import { useApolloClient, useMutation, useSubscription } from '@apollo/client/react'
import { Box, Dialog, DialogContent, Typography } from '@mui/material'
import type * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import {
  ActionQrScannedDocument,
  BasicUserFieldsFragmentDoc,
  GetCurrentUserDocument,
  UserLoginDocument,
} from '../gql/graphql'

gql`
  subscription ActionQrScanned($qrid: ID!) {
    actionQrScanned(qrid: $qrid) {
      qrid
      status
      userId
      openid
    }
  }

  mutation UserLogin($code: String, $openid: ID) {
    login(code: $code, openid: $openid) {
      authToken
      user {
        openid
        ...BasicUserFields
      }
    }
  }
`
interface WeappLoginProps {
  onLoginSuccess: () => void
}

interface WeappLoginState {
  qrid: string
  url: string
}

const WeappLogin: React.FC<WeappLoginProps> = ({ onLoginSuccess }) => {
  const [state, setState] = useState<WeappLoginState>()
  const apolloClient = useApolloClient()
  const hasLoggedIn = useRef(false)
  const [loginUser] = useMutation(UserLoginDocument)
  const { data } = useSubscription(ActionQrScannedDocument, {
    variables: { qrid: state?.qrid as string },
    skip: !state?.qrid,
  })

  useEffect(() => {
    // Only run this effect if we have data from the subscription and haven't logged in yet
    if (data?.actionQrScanned && !hasLoggedIn.current) {
      const { openid } = data.actionQrScanned

      ;(async () => {
        try {
          const { data } = await loginUser({
            variables: { openid },
          })

          if (!data?.login) {
            console.error('Login failed: No data received')
            return
          }

          localStorage.setItem('authToken', data.login.authToken)

          // Manually write to the cache
          apolloClient.writeQuery({
            query: GetCurrentUserDocument,
            data: {
              currentUser: apolloClient.cache.readFragment({
                id: apolloClient.cache.identify(data.login.user),
                fragment: BasicUserFieldsFragmentDoc,
              }),
            },
          })

          hasLoggedIn.current = true
          onLoginSuccess()
        } catch (apolloError) {
          console.error('Apollo error:', apolloError)
        }
      })()
    }
  }, [data, loginUser, apolloClient, onLoginSuccess])

  useEffect(() => {
    let objectUrl: string

    const fetchImage = async () => {
      const response = await fetch('/api/actionqr?action=login')
      objectUrl = URL.createObjectURL(await response.blob())
      const qrid = response.headers.get('Action-QR-Id')

      if (!qrid) {
        console.error('QR ID not found in response headers')
        return
      }

      setState({
        qrid,
        url: objectUrl,
      })
    }

    fetchImage()

    // Cleanup function: Revoke the Blob URL when the component unmounts
    // or when apiUrl changes to prevent memory leaks.
    return () => {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [])

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
      }}
    >
      <Typography variant="h6" component="div" sx={{ textAlign: 'center', mb: 2 }}>
        微信扫码登录
      </Typography>

      <Box
        component="img"
        src={state?.url}
        alt="login"
        sx={{
          width: 200,
          height: 200,
          borderRadius: 2,
          boxShadow: 3,
          cursor: 'pointer',
        }}
      />
    </Box>
  )
}

interface LoginDialogProps {
  onLoginSuccess: () => void
}

/**
 * A dialog to be used on a normal browser that prompts the user to log in by scanning
 * a weapp qrcode
 */
const LoginDialog: React.FC<LoginDialogProps> = ({ onLoginSuccess }) => {
  return (
    <Dialog
      open
      sx={{
        '& .MuiDialog-paperWidthSm': { width: 350 },
        marginLeft: 'auto',
        marginRight: 'auto',
        mt: 6,
      }}
    >
      <DialogContent>
        <WeappLogin onLoginSuccess={onLoginSuccess} />
      </DialogContent>
    </Dialog>
  )
}

export default LoginDialog
